import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useOrderData = () => {
  const [mesas, setMesas] = useState([]);
  const [products, setProducts] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [categoryMeta, setCategoryMeta] = useState({}); // name → { seccion, id, ... }

  const groupedCategories = useMemo(() => {
    const groups = { barra: [], cocina: [], tienda: [] };
    if (!categories || !Array.isArray(categories)) return groups;
    categories.forEach(catName => {
      const catProducts = products[catName] || [];
      if (catProducts.length === 0) return;

      // Explicit override from the category admin panel wins over the
      // majority vote. When the owner sets `seccion` on the category itself
      // we respect it verbatim so mixed-content categories (e.g. Café y
      // Plantas with 11 coffees + 4 rituals) can be placed intentionally.
      const explicit = categoryMeta[catName]?.seccion;
      if (explicit && groups[explicit]) {
        groups[explicit].push(catName);
        return;
      }

      // Fallback: majority-vote of products' seccion, tie-break → barra.
      const tally = { barra: 0, cocina: 0, tienda: 0 };
      catProducts.forEach(p => {
        const s = p?.seccion || 'cocina';
        if (tally[s] != null) tally[s] += 1;
      });
      let seccion = 'barra';
      let best = -1;
      ['barra', 'cocina', 'tienda'].forEach(k => {
        if (tally[k] > best) { best = tally[k]; seccion = k; }
      });
      if (groups[seccion]) groups[seccion].push(catName);
      else groups['cocina'].push(catName);
    });
    return groups;
  }, [categories, products, categoryMeta]);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [mesasRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/mesas/live`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/productos-rapidos`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (mesasRes.ok) setMesas(await mesasRes.json());
      if (productsRes.ok) {
        const data = await productsRes.json();
        const cats = [];
        const prodsByCat = {};
        const metaByCat = {};
        data.forEach(item => {
          const catName = item.category?.name?.es || item.category?.id || 'Sin categoria';
          cats.push(catName);
          prodsByCat[catName] = item.products || [];
          metaByCat[catName] = { id: item.category?.id, seccion: item.category?.seccion || null };
        });
        setCategories(cats);
        setProducts(prodsByCat);
        setCategoryMeta(metaByCat);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { mesas, products, categories, groupedCategories, loading };
};

export const getProductName = (product) => {
  if (!product?.name) return 'Producto';
  if (typeof product.name === 'string') return product.name;
  return product.name.es || product.name.en || product.name.cat || 'Producto';
};
